import { SlideFade } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Button, ChevronLeftOutline, ChevronRightOutline, Heading, HStack, IconButton, Portal, rem, Stack, Text, XOutline } from '@bonusly/design-system';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { useIsAnyPanelOpen } from '../../hooks/use-navbar-panels';
import { useIsProfileMenuOpen } from '../../hooks/use-profile-menu';
import { activePromosQueryKey, useActivePromos } from '../../queries/promos';
import { ActivePromo } from '../../schemas/promos';
import { useBizySidePanelStore } from '../bizy-side-panel/store/use-bizy-side-panel-store';
import { useUserContentDismissal } from '../profile/user-content-dismissal';

// Renders the promo's image / gif / video as a "cover" fill of its parent
// container. The parent owns the fixed height; we size the media to fully
// cover that slot (`w="100%" h="100%" objectFit="cover"`) so it always
// edges-out the container regardless of intrinsic aspect ratio — no
// letterbox / pillarbox bars on the sides or top.
const PromoMedia = ({ promo }: { promo: ActivePromo }) => {
	if (!promo.imageUrl) return null;

	if (promo.mediaType === 'video') {
		return (
			<Box
				as="video"
				src={promo.imageUrl}
				autoPlay
				loop
				muted
				playsInline
				w="100%"
				h="100%"
				display="block"
				// object-fit lives in `sx` (not the `objectFit` style prop) because
				// Chakra's style-prop mapping on `<Box as="video">` occasionally
				// drops it on certain browsers, leaving the native <video>
				// letterbox/pillarbox bars showing.
				sx={{
					objectFit: 'cover',
					objectPosition: 'center',
					'&::-webkit-media-controls': { display: 'none' },
				}}
			/>
		);
	}

	return <Box as="img" src={promo.imageUrl} alt="" w="100%" h="100%" display="block" sx={{ objectFit: 'cover', objectPosition: 'center' }} />;
};

// Matches Promos::Promo::DISMISSAL_NAMESPACE on the backend, which is also
// the default namespace for <PromoModal>. Keeping these in sync means the
// server-side filter in ListActivePromosForViewer naturally hides any promo
// dismissed via this card or any other surface that uses the same key.
const DISMISSAL_NAMESPACE = 'promo';

// Feature flag that gates the entire dynamic promo system on the FE. When
// off, no API call is made and the tile never renders, regardless of what
// the backend would return. NOTE: the Amplitude key is spelled with a typo
// (`annoucements`, missing an `n`); keep this constant in lockstep with
// whatever Amplitude has, or rename the flag in Amplitude first and update
// both at the same time.
//
// Exported because other surfaces (e.g. legacy office-hours cards) want
// to hide themselves when the dynamic promo system is taking over.
export const IN_APP_FEATURE_ANNOUNCEMENTS_FLAG = 'in-app-feature-annoucements';
const FEATURE_FLAG = IN_APP_FEATURE_ANNOUNCEMENTS_FLAG;

// Optional carousel controls passed in by <DynamicPromos> when there is
// more than one eligible promo. When present, the card renders prev/next
// chevrons and dot indicators so the user can step through the list.
interface CarouselControls {
	current: number;
	total: number;
	onPrev: () => void;
	onNext: () => void;
	onJumpTo: (index: number) => void;
}

interface DynamicPromoProps {
	promo: ActivePromo;
	carousel?: CarouselControls;
	// Whether the tile is currently visible to the user (i.e. the entrance
	// animation has run and no panel is covering it). Drives the `Viewed`
	// analytics event so we only count promos the user actually saw.
	isVisible: boolean;
}

// Renders a single dynamic promo as a floating teaser card. The CTA link
// opens the admin-configured CTA URL — in-app paths (starting with "/") in
// the same tab, full URLs in a new tab. Clicking it (or the X) persists a
// dismissal under the `promo:<name>` key so the backend will stop returning
// the promo on subsequent loads.
export const DynamicPromo = ({ promo, carousel, isVisible }: DynamicPromoProps) => {
	const { t } = useTranslation('components', { keyPrefix: 'dynamicPromo' });
	const { track } = useAnalytics();
	const queryClient = useQueryClient();

	const { updateUserContentDismissal } = useUserContentDismissal({
		name: promo.name,
		namespace: DISMISSAL_NAMESPACE,
	});

	// Identifies which promo a tracked interaction was on. Spread into every
	// analytics call so each event is attributable to a specific promo.
	const trackingProperties = { promoName: promo.name, promoId: promo.id };

	// Tracks which promo IDs have already fired a `Viewed` event during this
	// mount. Prevents a re-fire when the user navigates back to an already-
	// seen promo via the carousel, or when SlideFade reverses + re-runs
	// after a panel close.
	const viewedPromoIdsRef = useRef<Set<string>>(new Set());
	const ctaLabel = promo.ctaLabel?.trim() || t('learn_more');

	useEffect(() => {
		if (!isVisible) return;
		if (viewedPromoIdsRef.current.has(promo.id)) return;
		viewedPromoIdsRef.current.add(promo.id);
		track(AnalyticsEvent.DynamicPromo.Viewed, { promoName: promo.name, promoId: promo.id });
	}, [isVisible, promo.id, promo.name, track]);

	// Optimistically removes the card from the list so it unmounts without
	// waiting for the next refetch.
	const removeFromCache = () => {
		queryClient.setQueryData<ActivePromo[]>(activePromosQueryKey(), (current) => (current ?? []).filter((p) => p.id !== promo.id));
	};

	// Optimistically removing the promo from the cache is enough to hide the
	// tile: <DynamicPromos> will either re-render with the next promo (carousel
	// case) or short-circuit on `promos.length === 0` (last-promo case). We
	// deliberately don't keep a local `hasEngaged` flag here because this
	// component is reused across promos (no `key`), so a per-instance flag
	// would persist across dismissals and hide subsequent promos too.
	const persistDismissal = () => {
		updateUserContentDismissal();
		removeFromCache();
	};

	const handleTileDismiss = () => {
		track(AnalyticsEvent.DynamicPromo.Dismissed, trackingProperties);
		persistDismissal();
	};

	const handleLearnMore = () => {
		track(AnalyticsEvent.DynamicPromo.LearnMoreClicked, { ...trackingProperties, ctaUrl: promo.ctaUrl ?? null });
		persistDismissal();
	};

	const handleCarouselPrev = () => {
		track(AnalyticsEvent.DynamicPromo.CarouselNavigated, { ...trackingProperties, method: 'arrow', direction: 'previous' });
		carousel?.onPrev();
	};

	const handleCarouselNext = () => {
		track(AnalyticsEvent.DynamicPromo.CarouselNavigated, { ...trackingProperties, method: 'arrow', direction: 'next' });
		carousel?.onNext();
	};

	const handleCarouselJumpTo = (toIndex: number) => {
		track(AnalyticsEvent.DynamicPromo.CarouselNavigated, { ...trackingProperties, method: 'dot', toIndex });
		carousel?.onJumpTo(toIndex);
	};

	return (
		<Stack
			bgColor="bg.surface"
			borderRadius="md"
			boxShadow="lg"
			px={3}
			pt={promo.imageUrl ? 0 : 3}
			// Without carousel dots the CTA is the last child, so we need extra
			// breathing room at the bottom; with dots, the dots already provide
			// visual ballast and pb={4} is enough.
			pb={carousel ? 4 : 5}
			spacing={2}
			overflow="hidden"
			position="relative"
			data-testid={`dynamic-promo-${promo.name}`}
		>
			<Box position="absolute" top={2} right={2} zIndex={1}>
				<IconButton
					aria-label={t('dismiss')}
					icon={<XOutline boxSize={rem(14)} />}
					size="xs"
					variant="ghost"
					borderRadius="full"
					bg="secondary.bgAlphaActive"
					onClick={handleTileDismiss}
					data-testid={`dynamic-promo-${promo.name}-dismiss`}
				/>
			</Box>

			{promo.imageUrl && (
				<Box bg="bg.soft" mx={-3} h={rem(170)} overflow="hidden">
					<PromoMedia promo={promo} />
				</Box>
			)}

			<Heading size="md" color="text.default" noOfLines={2} pt={2} pr={promo.imageUrl ? 0 : 7}>
				{promo.title}
			</Heading>
			<Text size="sm" color="text.softer" noOfLines={3}>
				{promo.body}
			</Text>

			{promo.ctaUrl && (
				<Button
					as="a"
					href={promo.ctaUrl}
					// In-app paths (/rewards, /?seasonal-theme-modal=true) navigate in
					// the same tab; full URLs are external and open in a new one.
					{...(promo.ctaUrl.startsWith('/') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
					variant="link"
					size="sm"
					colorScheme="primary"
					alignSelf="flex-start"
					onClick={handleLearnMore}
					data-testid={`dynamic-promo-${promo.name}-open`}
				>
					{ctaLabel}
				</Button>
			)}

			{carousel && (
				<HStack spacing={1} justify="center" data-testid={`dynamic-promo-${promo.name}-carousel`}>
					<IconButton
						aria-label={t('previous')}
						icon={<ChevronLeftOutline boxSize={rem(14)} />}
						size="xs"
						variant="ghost"
						onClick={handleCarouselPrev}
						data-testid={`dynamic-promo-${promo.name}-prev`}
					/>
					<HStack spacing={1.5}>
						{Array.from({ length: carousel.total }).map((_, index) => (
							<Box
								key={index}
								as="button"
								aria-label={t('go_to', { number: index + 1 })}
								aria-current={index === carousel.current ? 'true' : undefined}
								w={2}
								h={2}
								borderRadius="full"
								bg={index === carousel.current ? 'primary.bg' : 'secondary.bgAlphaActive'}
								onClick={() => handleCarouselJumpTo(index)}
							/>
						))}
					</HStack>
					<IconButton
						aria-label={t('next')}
						icon={<ChevronRightOutline boxSize={rem(14)} />}
						size="xs"
						variant="ghost"
						onClick={handleCarouselNext}
						data-testid={`dynamic-promo-${promo.name}-next`}
					/>
				</HStack>
			)}
		</Stack>
	);
};

// Path of the bonus feed / home view. The floating tile is scoped to this
// route only; navigating elsewhere unmounts it (and re-mounts on return).
const HOME_PATH = '/';

// Sidebar widths (mirrors the constants in <Panel> and <AppLayout>). Used
// to compute the tile's horizontal offset so it sits a consistent gap past
// the sidebar's right edge, regardless of collapsed/expanded state.
const SIDEBAR_WIDTH_EXPANDED_PX = 260;
const SIDEBAR_WIDTH_COLLAPSED_PX = 60;
// Gap between the sidebar's right edge and the tile's left edge. Chakra's
// default spacing scale is 0.25rem per unit, so 4 units = 1rem = 16px.
const TILE_GAP_FROM_SIDEBAR_PX = 16;
// Matches the sidebar's own `width` transition in <AppLayout> so the tile
// slides horizontally in lockstep when the sidebar toggles.
const SIDEBAR_TRANSITION = 'left 0.2s cubic-bezier(.4,0,.2,1)';

// Local-dev affordance for iterating on placement / styling without needing
// the feature flag enabled or a real promo in the database. Activates only
// when the build is a dev build (`import.meta.env.DEV`) AND the URL contains
// `?dynamicPromoPreview=1`. Hard-gated to dev builds so this can never run
// in production. When active, bypasses both the feature flag check and the
// `/promos/active` fetch.
//
// The cc-topup video asset is pulled in via a dynamic `import()` rather than
// a top-level static import so Vite's DCE strips it out of production builds.
// `import.meta.env.DEV` is replaced with the literal `false` at build time,
// which lets esbuild/Rollup eliminate the entire branch (including the
// dynamic import) — otherwise the static asset would be copied into the
// production output even though nothing references it.
const useDynamicPromoPreviewPromos = (): ActivePromo[] | null => {
	const [previewPromos, setPreviewPromos] = useState<ActivePromo[] | null>(null);

	useEffect(() => {
		if (!import.meta.env.DEV) return;
		if (typeof window === 'undefined') return;
		const params = new URLSearchParams(window.location.search);
		if (params.get('dynamicPromoPreview') !== '1') return;

		let cancelled = false;
		void (async () => {
			const { default: ccTopupPromoVideo } = await import('../../assets/video/cc-topup-promo.mp4');
			if (cancelled) return;
			const now = new Date();
			const inAMonth = new Date(Date.now() + 1000 * 60 * 60 * 24 * 30);
			setPreviewPromos([
				{
					id: 'preview-1',
					name: 'preview-with-image',
					title: 'Your team data, without the spreadsheet',
					body: 'Managers can now set their own reporting relationships without an HRIS integration or CSV upload. Admins stay in control with approval settings.',
					imageUrl: ccTopupPromoVideo,
					mediaType: 'video',
					ctaLabel: 'Learn more',
					ctaUrl: 'https://bonus.ly',
					startsAt: now,
					endsAt: inAMonth,
				},
				{
					id: 'preview-2',
					name: 'preview-no-image',
					title: 'A promo without any media',
					body: 'When there is no image, the close button sits on the white card surface and the headline pulls up to the top.',
					imageUrl: null,
					mediaType: null,
					ctaLabel: null,
					ctaUrl: null,
					startsAt: now,
					endsAt: inAMonth,
				},
				{
					id: 'preview-3',
					name: 'preview-long-copy',
					title: 'A much longer promo title that will get clamped to two lines by the noOfLines prop on the heading',
					body: 'And the body is also longer than usual to make sure the truncation looks right when the copy gets carried away. We should still see the Learn more button below it.',
					imageUrl: ccTopupPromoVideo,
					mediaType: 'video',
					ctaLabel: 'Learn more',
					ctaUrl: 'https://bonus.ly',
					startsAt: now,
					endsAt: inAMonth,
				},
			]);
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	return previewPromos;
};

interface DynamicPromosProps {
	// Whether the navigation sidebar is currently expanded. Drives the
	// tile's horizontal offset so it always sits the same gap past the
	// sidebar's right edge. Defaults to `true` for callers (e.g. tests)
	// that don't render inside the navbar; on the real page <UserNavbar>
	// threads its own `isExpanded` through.
	isSidebarExpanded?: boolean;
}

// Renders the active promo (or carousel of active promos) as a floating
// bottom-left tile, scoped to the home route. Tracks the sidebar's
// collapsed/expanded state so the tile always sits a consistent gap past
// the sidebar's right edge. Hides itself whenever any navbar panel
// (Rewards / Celebrations / Leaderboard / Team / Notifications), the
// Bizy side panel, or the user-profile menu is open, since those
// surfaces sit at a higher z-index and would otherwise cover the tile.
// Waits for `window.load` before the entrance animation so the tile
// doesn't compete with initial page paint.
export const DynamicPromos = ({ isSidebarExpanded = true }: DynamicPromosProps = {}) => {
	const isFeatureEnabled = useFeatureFlag(FEATURE_FLAG);
	const { pathname } = useLocation();
	const isHomePage = pathname === HOME_PATH;
	const previewPromos = useDynamicPromoPreviewPromos();
	const { data: fetchedPromos } = useActivePromos({ enabled: isFeatureEnabled && isHomePage && !previewPromos });
	const promos = previewPromos ?? fetchedPromos;
	const [currentIndex, setCurrentIndex] = useState(0);
	const [hasPageLoaded, setHasPageLoaded] = useState(false);
	const isAnyPanelOpen = useIsAnyPanelOpen();
	const isBizyPanelOpen = useBizySidePanelStore((state) => state.isOpen);
	const isProfileMenuOpen = useIsProfileMenuOpen();
	// SlideFade reverses its entrance transition when this flips back to
	// false, so closing a panel slides the tile back into place.
	const isVisible = hasPageLoaded && !isAnyPanelOpen && !isBizyPanelOpen && !isProfileMenuOpen;

	// Clamp the index when the visible list shrinks (dismissal) or changes.
	useEffect(() => {
		if (!promos?.length) return;
		if (currentIndex > promos.length - 1) setCurrentIndex(promos.length - 1);
	}, [promos, currentIndex]);

	// Wait until the rest of the page has finished loading before the tile
	// slides on. If the window `load` event has already fired by the time
	// the promos fetch resolves (the common case in an SPA), animate
	// immediately; otherwise listen once and trigger when it fires.
	useEffect(() => {
		if (!promos?.length) return;
		if (document.readyState === 'complete') {
			setHasPageLoaded(true);
			return;
		}
		const onLoad = () => setHasPageLoaded(true);
		window.addEventListener('load', onLoad);
		return () => window.removeEventListener('load', onLoad);
	}, [promos]);

	if (!isHomePage) return null;
	if (!previewPromos && !isFeatureEnabled) return null;
	if (!promos?.length) return null;

	const safeIndex = Math.min(currentIndex, promos.length - 1);
	const currentPromo = promos[safeIndex];
	const total = promos.length;

	const carousel: CarouselControls | undefined =
		total > 1
			? {
					current: safeIndex,
					total,
					onPrev: () => setCurrentIndex((i) => (i <= 0 ? total - 1 : i - 1)),
					onNext: () => setCurrentIndex((i) => (i + 1) % total),
					onJumpTo: setCurrentIndex,
				}
			: undefined;

	const sidebarWidthPx = isSidebarExpanded ? SIDEBAR_WIDTH_EXPANDED_PX : SIDEBAR_WIDTH_COLLAPSED_PX;
	const leftOffsetPx = sidebarWidthPx + TILE_GAP_FROM_SIDEBAR_PX;

	return (
		<Portal>
			<Box
				position="fixed"
				bottom={4}
				left={`${leftOffsetPx}px`}
				width={rem(320)}
				zIndex="sticky"
				transition={SIDEBAR_TRANSITION}
				// SlideFade animates the tile out by fading opacity to 0 but
				// keeps it in the DOM (`unmountOnExit={false}`) so panel-toggle
				// re-entrances are smooth. Without `pointerEvents: none` while
				// out-of-view, the invisible 320×~250 region would still
				// capture clicks meant for the bonus feed behind it. Same idea
				// for `aria-hidden` — the tile shouldn't be announced or
				// focusable while it's animated out.
				pointerEvents={isVisible ? 'auto' : 'none'}
				aria-hidden={!isVisible}
			>
				<SlideFade in={isVisible} offsetY={24} unmountOnExit={false}>
					<DynamicPromo promo={currentPromo} carousel={carousel} isVisible={isVisible} />
				</SlideFade>
			</Box>
		</Portal>
	);
};
